import { CurrentSupplierType } from "features/contracting/enums/currentSupplierType";
import { ContractingProcessAnswerProps } from "features/contracting/models/ContractingProcessAnswerProps";
import PageCard from "features/shared/components/card/PageCard";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SupplierSelect from "features/contracting/components/SupplierSelect";
import {
  setCurrentSupplierValidation,
  setNoContractByAnotherValidations,
} from "features/contracting/sections/contractingForms/processForm/processFormValidation";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { Commodity } from "features/shared/enums/commodity";
import { useAfterInit } from "features/shared/hooks/useAfterInit";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

interface Props extends ContractingProcessAnswerProps {}

const NoContractByAnotherSubForm = ({ formHandler }: Props) => {
  // translations
  const tTypes = useTranslation("contracting\\enums\\currentSupplierType").t;
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "noContractByAnotherSubForm");

  // store
  const configuration = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).configuration;

  const commodity = useContractingCustomerTypeValue<Commodity>("commodity");

  const commoditySuppliers = configuration.suppliers!.filter(
    (x) => x.commodity === commodity
  );

  const suppliers: UserEnumDTO[] = commoditySuppliers.filter(
    (x) => !x.isParentCompany
  );
  
  const currentSupplier: UserEnumDTO | undefined = commoditySuppliers.find(
    (x) => x.isParentCompany
  );

  // form hook props
  const {
    bindReactSelectField,
    bindCheckField,
    clearFormValues,
    setValidationRule,
    errors,
    getFormValue,
    setFormValue,
  } = { ...formHandler };

  // after init hook
  const afterInit = useAfterInit();

  // side-effects
  useEffect(() => {
    // set default validations
    setNoContractByAnotherValidations(setValidationRule);

    return () => {
      // before unmount component remove values and validations
      setNoContractByAnotherValidations(setValidationRule, "remove");
      clearFormValues(["noContractByAnother"]);
    };
  }, []);

  // automatic setting current supplier code
  useEffect(() => {
    if (afterInit) {
      const supplierType = getFormValue<CurrentSupplierType>(
        "noContractByAnother.currentSupplierType"
      );

      if (supplierType === "Same") {
        setFormValue(
          "noContractByAnother.currentSupplierCode",
          currentSupplier?.code
        );
      } else {
        clearFormValues(["noContractByAnother.currentSupplierCode"]);
      }
    }
  }, [
    getFormValue<CurrentSupplierType>(
      "noContractByAnother.currentSupplierType"
    ),
  ]);

  // setting current supplier select validation
  useEffect(() => {
    if (showSupplierSelect()) {
      setCurrentSupplierValidation(setValidationRule);
    } else {
      setCurrentSupplierValidation(setValidationRule, "remove");
    }

    return () => {
      setCurrentSupplierValidation(setValidationRule, "remove");
    };
  }, [
    getFormValue<CurrentSupplierType>(
      "noContractByAnother.currentSupplierType"
    ),
  ]);

  // helpers
  const getSupplierTypeTitle = (type: string) => {
    if (type === CurrentSupplierType.Same) {
      return currentSupplier?.name!;
    } else {
      return tTypes(`${type}.title`);
    }
  };

  const showSupplierSelect = () => {
    const supplierType = getFormValue<CurrentSupplierType>(
      "noContractByAnother.currentSupplierType"
    );
    return supplierType === "Other";
  };

  // sub-renders
  const renderTypes = () => {
    const processTypes = Object.keys(CurrentSupplierType).filter(
      (x) => x !== "Undefined"
    );

    return processTypes.map((type) => (
      <div className="col-md-6 px-2" key={type}>
        <RadioBox
          label={getSupplierTypeTitle(type)}
          text={tTypes(`${type}.subtitle`)}
          icon={{ icon: "comment-dots" }}
          {...bindCheckField("noContractByAnother.currentSupplierType", type)}
          className="mb-lg-0"
        />
      </div>
    ));
  };

  // render
  return (
    <PageCard
      title={tLabels("currentSupplierCardTitle")}
      bodyClassName="pt-4 px-4"
      cardClassName="mb-4"
    >
      <FormGroup
        labelText={tLabels("currentSupplierType")}
        validationError={errors["noContractByAnother.currentSupplierType"]}
      >
        <div className="row no-gutters mx-n2">{renderTypes()}</div>
      </FormGroup>
      <SupplierSelect
        name="noContractByAnother.currentSupplierCode"
        suppliers={suppliers}
        bindReactSelectField={bindReactSelectField}
        errors={errors}
        visible={showSupplierSelect()}
      />
    </PageCard>
  );
};

export default NoContractByAnotherSubForm;
